import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import SystemComponentsTable from 'components/systems/system-components-table';
import { SystemOptionType } from 'data/types';
import { useComparisonSelection } from 'hooks/use-comparison-selection';
import { formatPrice } from 'utils/helpers';
import EditableSystemName from './editable-system-name';
import { PickKind, pickSystemNameFor } from './equipment-comparison-utils';
import ScoreCategoryTooltip from './score-category-tooltip';
import { SCORE_CATEGORY_DEFINITIONS } from './score-category-labels';
import ScoreChip from './score-chip';
import ScorePenaltiesBonuses from './score-penalties-bonuses';
import { getSystemConfigurationSummaryLines } from './system-configuration-summary';
import SystemFloorplan from './system-floorplan';
import SystemOverviewBand from './system-overview-band';
import SystemRecommendationReasons from './system-recommendation-reasons';
import SystemTitleTags from './system-title-tags';
import ZonePerformanceTable from './zone-performance-table';

type EquipmentComparisonMiddlePanelProps = Readonly<{
  system: SystemOptionType | null;
  pickKind: PickKind | null;
  currentIndex: number;
  onCustomSystemSaved?: (uniqueId: string) => void;
}>;

// TODO: clarify dynamic description source — the mockup shows a curated
// blurb (e.g. "The best balance of value, comfort, and reliability with
// built-in backup protection."). For now we leave the description slot empty.

const resolveSystemName = (
  system: SystemOptionType,
  pickKind: PickKind | null,
  currentIndex: number
): string => {
  if (pickKind) return pickSystemNameFor(pickKind);
  const curated = system.name?.trim();
  if (curated) return curated;
  return `System ${currentIndex + 1}`;
};

function ScoreChipsRow({ system }: { system: SystemOptionType }) {
  const { score } = system;
  if (!score) return null;
  const { categoryScores, total } = score;
  return (
    <div className='grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(130px,1fr))]'>
      {SCORE_CATEGORY_DEFINITIONS.map(({ key, label, icon }) => (
        <ScoreCategoryTooltip
          key={key}
          categoryKey={key}
          breakdown={categoryScores[key]?.breakdown}
        >
          <ScoreChip
            label={label}
            value={categoryScores[key]?.score ?? 0}
            icon={icon}
          />
        </ScoreCategoryTooltip>
      ))}
      <ScoreChip
        label='Overall Score'
        value={total}
        emphasis
        icon={SparklesIcon}
      />
    </div>
  );
}

export default function EquipmentComparisonMiddlePanel({
  system,
  pickKind,
  currentIndex,
  onCustomSystemSaved
}: EquipmentComparisonMiddlePanelProps) {
  const developerMode = useAppSelector((state) => state.ui.developerMode);
  const { toggleSystem, isInComparison } = useComparisonSelection();

  if (!system) {
    return (
      <div className='flex h-full items-center justify-center text-gray-400'>
        Select a system to view details.
      </div>
    );
  }

  const configName = system.configuration.name?.trim();
  const showTopMatch = pickKind === 'bestOverall';
  const { condenserLine, zoneLine } =
    getSystemConfigurationSummaryLines(system);
  const alreadyChosen = isInComparison(system.id);

  return (
    <div className='flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <div className='sticky top-0 z-10 -mx-4 -mt-4 flex flex-col gap-4 rounded-t-lg border-b border-gray-200 bg-white px-4 pb-4 pt-4'>
        <div className='flex items-center gap-4'>
          <div className='flex min-w-0 grow flex-col gap-1'>
            {configName && (
              <div className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
                Configuration: {configName}
              </div>
            )}
            <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
              <EditableSystemName
                system={system}
                displayName={resolveSystemName(system, pickKind, currentIndex)}
                showTopMatch={false}
              />
              <span className='text-xl font-bold text-gray-900'>
                {formatPrice(system.price)}
              </span>
            </div>
            <SystemTitleTags system={system} showTopMatch={showTopMatch} />
            {!developerMode && (
              <div className='flex flex-col text-sm text-gray-600'>
                <div>{condenserLine}</div>
                <div>{zoneLine}</div>
              </div>
            )}
          </div>
          <div className='shrink-0'>
            <Button
              color={alreadyChosen ? 'green' : 'primary'}
              text={alreadyChosen ? 'Added to Comparison' : 'Add to Comparison'}
              icon={alreadyChosen ? CheckIcon : undefined}
              iconSize='sm'
              onClick={() => toggleSystem(system, pickKind)}
            />
          </div>
        </div>

        {developerMode && (
          <>
            <ScoreChipsRow system={system} />
            <ScorePenaltiesBonuses score={system.score} />
          </>
        )}
      </div>

      <SystemOverviewBand system={system} />

      <SystemRecommendationReasons system={system} />

      <div>
        <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500'>
          Zoning &amp; Layout
        </div>
        <SystemFloorplan system={system} />
      </div>

      <div>
        <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500'>
          System Components
        </div>
        <SystemComponentsTable
          option={system}
          onCustomSystemSaved={onCustomSystemSaved}
        />
      </div>

      <ZonePerformanceTable system={system} />
    </div>
  );
}
